import { useId } from "react";

import {
  DollarIcon, FacebookIcon, InstagramIcon, RedditIcon, SparkIcon, StarsIcon, TelegramIcon, TiktokIcon, XIcon, seriesPoints, segments, smoothPath,
} from "@signet/ui";
import { api, CreatorAvatar, NowPanelHead, NowStat, nowDay, nowRangeQuery, nowRangeVersus, nowRangeWords, submittedWords, type NowRange, useCreatorPhotos, useSeenQuery } from "@signet/web-kit";
import { money } from "./api.ts";
import { RLink } from "./shared.tsx";

/* ──────────────────────────────────────────────────────────────────────────
   Reporting on the Now page: the Revenue figure, and revenue by day and by
   dashboard. All of it follows the range picked at
   the top of the page (Jay, 2026-10-07).

   Today and Yesterday are Melbourne calendar days. Until that day's export
   lands they read $0 with a "No data for today" chip (Jay, 2026-10-13), in
   place of the newest figure under a "More than 24 hours old" chip.
   ────────────────────────────────────────────────────────────────────────── */

type AsOf = { date: string; importedAt: string; stale: boolean };
type Revenue =
  | { available: false; currency: string }
  | { available: true; currency: string; value: number | null; previous: number | null; from: string | null; to: string | null; asOf: AsOf; spark: number[]; noData: boolean; submittedAt: string | null };
type Panel = {
  currency: string;
  today: string;
  asOf: AsOf | null;
  total: number | null;
  noData: boolean;
  days: { date: string; earnings: number | null; estimated: boolean }[];
  // The top three creators who earned on each dashboard; missing on an older API answer.
  dashboards: { name: string; platform: Platform | null; earnings: number; creators?: { creator: string; earnings: number }[] }[];
};
type Platform = "instagram" | "reddit" | "telegram" | "facebook" | "twitter" | "tiktok" | "sfs";

// The platform a dashboard is for, as its icon. SFS is stars: it is not a
// platform but creators promoting each other.
const PLATFORM_ICON: Record<Platform, typeof InstagramIcon> = {
  instagram: InstagramIcon,
  reddit: RedditIcon,
  telegram: TelegramIcon,
  facebook: FacebookIcon,
  twitter: XIcon,
  tiktok: TiktokIcon,
  sfs: StarsIcon,
};

/** The chip for a day nothing has landed for yet. */
export const noDataChip = (range: NowRange) => (range.key === "yesterday" ? "No data for yesterday" : "No data for today");

function forDays(from: string | null, to: string | null): string | null {
  if (!from || !to) return null;
  return from === to ? `For ${nowDay(to)}` : `For ${nowDay(from)} to ${nowDay(to)}`;
}

const dayBefore = (day: string) => new Date(Date.parse(`${day}T00:00:00Z`) - 86_400_000).toISOString().slice(0, 10);

/**
 * The reporting page over the days a card counted. A custom period there runs
 * from one evening's snapshot to another, so the days D1 to D2 are the period
 * from the day before D1 (the same request now-stats makes for the card).
 */
function reportingPeriod(range: NowRange, from: string | null, to: string | null): Record<string, string> | undefined {
  if (range.key === "7d") return { period: "7D" };
  if (range.key === "30d") return { period: "30D" };
  return from && to ? { period: "CUSTOM", from: dayBefore(from), to } : undefined;
}

export function RevenueNow({ range }: { range: NowRange }) {
  const revenue = useSeenQuery({
    queryKey: ["reporting", "now-stats", nowRangeQuery(range)],
    queryFn: () => api<Revenue>(`/api/m/reporting/now-stats?${nowRangeQuery(range)}`),
    refetchInterval: 5 * 60_000,
  });
  const data = revenue.data;
  const ready = data?.available ? data : null;
  // Today is the newest day the export has landed for, which the caption
  // under the figure names; Jay wants the label to just say today (2026-10-07).
  return (
    <NowStat
      icon={<DollarIcon size={18} />}
      label={`Revenue ${nowRangeWords(range)}`}
      colour="var(--violet)"
      value={data && !data.available ? "No figures yet" : ready ? (ready.value === null ? "Not enough data" : money(ready.value, ready.currency)) : null}
      empty={Boolean(data && (!data.available || data.value === null))}
      current={ready?.value}
      previous={ready?.previous}
      versus={nowRangeVersus(range)}
      spark={ready?.spark}
      chip={ready?.noData ? noDataChip(range) : null}
      // When the day's export came in; only Today and Yesterday carry it (Jay, 2026-10-15).
      note={submittedWords(ready?.submittedAt)}
      caption={ready ? forDays(ready.from, ready.to) : null}
      failed={revenue.isError}
      // The summary over the same period the card counted (Jay, 2026-10-13): 7D and 30D are the
      // page's own periods, which these cards already take their figures from; a day or picked dates are custom.
      href="/reporting"
      search={reportingPeriod(range, ready?.from ?? null, ready?.to ?? null)}
    />
  );
}

const weekday = (date: string, today: string) =>
  date === today ? "Today" : new Date(`${date}T00:00:00Z`).toLocaleDateString(undefined, { weekday: "short", timeZone: "UTC" });

/** Weekdays for a week or less; past that a weekday no longer says which week, so dates, and only some of them. */
const dayLabel = (date: string, today: string, count: number) => (count <= 7 ? weekday(date, today) : nowDay(date));
const labelEvery = (count: number) => (count <= 8 ? 1 : Math.ceil(count / 7));

const WEEK_CHART = { width: 700, height: 150, padding: 22 };

/**
 * The range as a line, drawn like the Revenue, Visitors and Clicks cards above
 * it (Jay, 2026-09-30): a smooth violet line over a soft fill, scaled from
 * zero with the same headroom. A day with no figures yet is a gap, so the line
 * stops at the latest export instead of diving to zero for days to come.
 */
function RangeLine({ days, today, currency }: { days: Panel["days"]; today: string; currency: string }) {
  const gradient = useId();
  const values = days.map((day) => day.earnings);
  const known = values.filter((v): v is number => v !== null);
  const high = Math.max(0, ...known);
  const low = Math.min(0, ...known);
  const span = high - low || 1;
  const top = high + span * 0.14;
  const bottom = low - span * 0.05;
  const points = seriesPoints({ values: values.map((v) => (v === null ? null : v - bottom)), max: top - bottom, ...WEEK_CHART });
  const runs = segments(points).filter((run) => run.length > 1);
  const { width, height } = WEEK_CHART;
  const at = (x: number, y: number) => ({ left: `${(x / width) * 100}%`, top: `${(y / height) * 100}%` });

  return (
    <div className="rp-week-line" role="list" aria-label="Revenue by day">
      <div className="rp-week-plot">
        <svg viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" aria-hidden="true">
          <defs>
            <linearGradient id={gradient} x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="var(--violet)" stopOpacity=".32" />
              <stop offset="100%" stopColor="var(--violet)" stopOpacity="0" />
            </linearGradient>
          </defs>
          {runs.map((run, i) => {
            const line = smoothPath(run);
            return (
              <g key={i}>
                <path d={`${line} L ${run.at(-1)!.x} ${height} L ${run[0]!.x} ${height} Z`} fill={`url(#${gradient})`} />
                <path d={line} fill="none" stroke="var(--violet)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
              </g>
            );
          })}
        </svg>
        {days.map((day, i) => {
          const point = points[i];
          if (!point) return null;
          const label = `${dayLabel(day.date, today, days.length)}: ${money(day.earnings!, currency)}${day.estimated ? ", estimated across a gap between exports" : ""}`;
          return (
            <span
              key={day.date}
              className={`rp-week-dot${day.date === today ? " today" : ""}${day.estimated ? " estimated" : ""}`}
              style={at(point.x, point.y)}
              title={label}
              aria-hidden="true"
            />
          );
        })}
      </div>
      <div className="rp-week-labels">
        {days.map((day, i) => {
          const future = day.date > today;
          const x = WEEK_CHART.padding + (i * (width - WEEK_CHART.padding * 2)) / Math.max(1, days.length - 1);
          const label = `${dayLabel(day.date, today, days.length)}: ${day.earnings === null ? (future ? "still to come" : "no figures yet") : money(day.earnings, currency)}${day.estimated ? ", estimated across a gap between exports" : ""}`;
          const shown = i % labelEvery(days.length) === 0 || i === days.length - 1;
          return (
            <span key={day.date} role="listitem" aria-label={label} className={`rp-week-label${day.date === today ? " today" : ""}${shown ? "" : " rp-hidden"}`} style={{ left: `${(x / width) * 100}%` }}>
              {dayLabel(day.date, today, days.length)}
            </span>
          );
        })}
      </div>
    </div>
  );
}

function useRevenuePanel(range: NowRange) {
  return useSeenQuery({
    queryKey: ["reporting", "now-revenue", nowRangeQuery(range)],
    queryFn: () => api<Panel>(`/api/m/reporting/now-revenue?${nowRangeQuery(range)}`),
    refetchInterval: 5 * 60_000,
  });
}

/** $2.1K from a thousand up, whole units under it, so four creators fit on a dashboard's line. */
function shortMoney(value: number, currency: string): string {
  if (Math.abs(value) < 1000) return money(value, currency);
  const thousands = Math.round(value / 100) / 10;
  return `${money(0, currency).replace(/0$/, "")}${thousands}K`;
}

export function RevenuePanel({ range }: { range: NowRange }) {
  const panel = useRevenuePanel(range);
  const photos = useCreatorPhotos("/api/m/reporting");
  const data = panel.data;
  const dashboardPeak = Math.max(1, ...(data?.dashboards ?? []).map((dashboard) => dashboard.earnings));
  // A line needs two days to be a line; for one day the figure and the dashboards say it all.
  const line = (data?.days.length ?? 0) > 1;

  return (
    <section className="panel rp-week">
      <NowPanelHead
        icon={<SparkIcon size={12} />}
        label="Signet Pulse"
        // The total sits beside the title, on the left (Ben, 2026-10-31): far right it was easy to miss.
        title={
          <>
            Revenue {nowRangeWords(range)}
            {data && data.total !== null ? <b className="rp-week-total tabular">{money(data.total, data.currency)}</b> : null}
          </>
        }
        sub={(data?.days.length ? forDays(data.days[0]!.date, data.days.at(-1)!.date) : null) ?? "Across every dashboard."}
        side={data?.noData ? <span className="rp-week-head-side"><span className="signet-stat-chip">{noDataChip(range)}</span></span> : null}
      />
      <div className="panel-body">
        {panel.isError ? <p className="faint">Couldn't load revenue for this range.</p> : null}
        {data ? (
          <>
            {line ? <RangeLine days={data.days} today={data.today} currency={data.currency} /> : null}

            <h3 className="rp-week-sub">Revenue by dashboard</h3>
            {data.dashboards.length ? (
              <ul className="rp-week-channels">
                {data.dashboards.map((dashboard) => {
                  const Icon = dashboard.platform ? PLATFORM_ICON[dashboard.platform] : null;
                  return (
                    <li key={dashboard.name}>
                      <span className="rp-week-channel">
                        <span className="rp-week-platform" aria-hidden="true">{Icon ? <Icon size={17} /> : null}</span>
                        <span className="rp-week-channel-name">{dashboard.name}</span>
                      </span>
                      <span className="rp-week-channel-track"><span style={{ width: `${Math.max(2, (dashboard.earnings / dashboardPeak) * 100)}%` }} /></span>
                      <b className="tabular">{money(dashboard.earnings, data.currency)}</b>
                      {/* Who the money came from on this dashboard, and how much (Ben, 2026-10-29): "IG MAIN: JACKIE ($2.1K) BELLA ($500)". */}
                      {dashboard.creators?.length ? (
                        <span className="rp-week-channel-creators">
                          {dashboard.creators.map((row) => (
                            <span key={row.creator} className="rp-week-channel-creator" title={`${row.creator}: ${money(row.earnings, data.currency)}`}>
                              <CreatorAvatar name={row.creator} src={photos(row.creator)} size={20} />
                              {row.creator}
                              <span className="rp-week-channel-share tabular">({shortMoney(row.earnings, data.currency)})</span>
                            </span>
                          ))}
                        </span>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="faint rp-small">Nothing in this range yet. The first figures land with the next export.</p>
            )}
            <RLink to="/reporting" className="text-button tiny accent">Open reporting</RLink>
          </>
        ) : null}
      </div>
    </section>
  );
}
